<template>
  <div class="import-row-table-wrap">
    <table class="table is-fullwidth is-narrow is-striped import-row-table" v-if="displayColumns.length">
      <thead>
        <tr>
          <th v-for="c in displayColumns" :key="c.key">{{ c.label }}</th>
          <th v-if="hasCreatable">Will create</th>
          <th class="import-row-actions" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in rowStates" :key="row._key">
          <td v-for="c in displayColumns" :key="c.key">
            <b-field v-if="c.key === 'catalogItemType'" :type="fieldType(row, c.key)">
              <div
                v-if="fieldErrorText(row, c.key)"
                class="import-err-hover"
                :title="fieldErrorText(row, c.key)"
                @mouseenter="onErrEnter($event, fieldErrorText(row, c.key))"
                @mouseleave="onErrLeave"
              >
                <p
                  class="is-size-7 has-text-weight-medium has-background-light p-1 import-type-readonly import-type-td"
                >
                  {{ typeLabelForSlug(row.record[c.key]) }}
                </p>
              </div>
              <p
                v-else
                class="is-size-7 has-text-weight-medium has-background-light p-1 import-type-readonly import-type-td"
              >
                {{ typeLabelForSlug(row.record[c.key]) }}
              </p>
            </b-field>
            <b-field v-else :type="fieldType(row, c.key)">
              <div
                v-if="fieldErrorText(row, c.key)"
                class="import-err-hover"
                :title="fieldErrorText(row, c.key)"
                @mouseenter="onErrEnter($event, fieldErrorText(row, c.key))"
                @mouseleave="onErrLeave"
              >
                <b-input
                  :value="row.record[c.key]"
                  size="is-small"
                  @input="onFieldInput(idx, c.key, $event)"
                  @blur="onFieldBlur(idx, c.key)"
                />
              </div>
              <b-input
                v-else
                :value="row.record[c.key]"
                size="is-small"
                @input="onFieldInput(idx, c.key, $event)"
                @blur="onFieldBlur(idx, c.key)"
              />
            </b-field>
          </td>
          <td v-if="hasCreatable">
            <span v-if="creatableFlags(row).length" class="is-size-7">{{ creatableFlags(row).join(', ') }}</span>
          </td>
          <td class="import-row-actions is-narrow">
            <b-button
              type="is-danger"
              class="is-light"
              size="is-small"
              icon-left="trash"
              title="Remove row"
              @click="emitRemove(row)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <div
      v-show="errTip.show"
      class="import-err-floater"
      :style="errTipBoxStyle"
      @mouseenter="onFloaterEnter"
      @mouseleave="onFloaterLeave"
    >
      {{ errTip.text }}
    </div>
  </div>
</template>

<script>
import { CatalogItemImport } from '@newmoon-org/shared';

const { TYPES } = CatalogItemImport;

export default {
  name: 'ImportRowEditorTable',
  props: {
    displayColumns: {
      type: Array,
      default: () => [],
    },
    rowStates: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      errTip: { show: false, text: '', left: 0, top: 0, width: 0 },
      errTipHideTimer: null,
    };
  },
  computed: {
    hasCreatable() {
      return (this.rowStates[0] && this.rowStates[0].creatable) != null;
    },
    errTipBoxStyle() {
      const t = this.errTip;
      return {
        left: `${t.left}px`,
        top: `${t.top}px`,
        maxWidth: t.width ? `${t.width}px` : '18rem',
      };
    },
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onTableScroll, true);
  },
  mounted() {
    window.addEventListener('scroll', this.onTableScroll, true);
  },
  methods: {
    onTableScroll() {
      if (this.errTip.show) {
        this.clearErrTip();
      }
    },
    clearErrTip() {
      if (this.errTipHideTimer) {
        clearTimeout(this.errTipHideTimer);
        this.errTipHideTimer = null;
      }
      this.errTip = { show: false, text: '', left: 0, top: 0, width: 0 };
    },
    onErrEnter(e, text) {
      if (!text) return;
      if (this.errTipHideTimer) {
        clearTimeout(this.errTipHideTimer);
        this.errTipHideTimer = null;
      }
      const r = e.currentTarget.getBoundingClientRect();
      const w = Math.min(420, Math.max(200, r.width, window.innerWidth - r.left - 16));
      const left = r.left;
      const top = r.top;
      this.errTip = { show: true, text, left, top, width: w };
    },
    onErrLeave() {
      this.errTipHideTimer = setTimeout(() => {
        this.clearErrTip();
      }, 200);
    },
    onFloaterEnter() {
      if (this.errTipHideTimer) {
        clearTimeout(this.errTipHideTimer);
        this.errTipHideTimer = null;
      }
    },
    onFloaterLeave() {
      this.clearErrTip();
    },
    typeLabelForSlug(v) {
      const s = v != null ? String(v).toLowerCase() : '';
      const o = TYPES.find(t => t.value === s);
      if (o) return o.label;
      if (v == null || v === '') return '—';
      return String(v);
    },
    creatableFlags(row) {
      if (!row.creatable) return [];
      return Object.keys(row.creatable).filter(k => row.creatable[k]);
    },
    fieldType(row, k) {
      if (!row.fieldErrors || !row.fieldErrors[k]) return null;
      return 'is-danger';
    },
    fieldErrorText(row, k) {
      if (!row.fieldErrors || !row.fieldErrors[k]) return '';
      return String(row.fieldErrors[k]);
    },
    onFieldInput(idx, key, v) {
      if (key === 'catalogItemType') return;
      this.$emit('field-input', { idx, key, value: v });
    },
    onFieldBlur(idx, key) {
      if (key === 'catalogItemType') return;
      this.$emit('field-blur', { idx, key });
    },
    emitRemove(row) {
      this.$emit('remove-row', { _key: row._key });
    },
  },
};
</script>

<style scoped>
.import-row-table-wrap {
  overflow-x: auto;
  max-width: 100%;
  position: relative;
}
.import-row-table td {
  min-width: 6rem;
  vertical-align: top;
}
.import-type-td {
  min-width: 6rem;
  max-width: 8rem;
  margin-bottom: 0;
  white-space: nowrap;
  vertical-align: middle;
}
.import-row-actions {
  width: 3rem;
  text-align: center;
  vertical-align: middle;
}
.import-err-hover {
  display: block;
  width: 100%;
  cursor: help;
}
.import-err-floater {
  position: fixed;
  z-index: 20050;
  padding: 0.4rem 0.55rem;
  font-size: 0.75rem;
  line-height: 1.35;
  text-align: left;
  color: #4a1a1a;
  background: #ffeff1;
  border: 1px solid #f14668;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(10, 10, 10, 0.2);
  transform: translateY(calc(-100% - 6px));
  white-space: normal;
  word-break: break-word;
  pointer-events: auto;
  max-width: min(24rem, calc(100vw - 1rem));
}
@media screen and (max-width: 768px) {
  .import-row-table-wrap {
    -webkit-overflow-scrolling: touch;
  }
  .import-row-table td {
    min-width: 5rem;
  }
}
</style>
